/**
 * PixiEngine — PhaserGame.jsx 대체
 *
 * Phaser.Game → PIXI.Application 래핑
 * 기존 코드의 game.registry, game.scene 등을 호환하는 얇은 레이어.
 */
import { Application, Container, EventEmitter } from 'pixi.js';

export class PixiEngine {
    constructor() {
        /** @type {Application} */
        this.app = null;
        /** @type {Map<string, import('./SceneCompat').SceneCompat>} */
        this.scenes = new Map();
        /** @type {Map<string, any>} */
        this._registry = new Map();
        this.isRunning = false;
        this.canvas = null;
        this._activeSceneKey = null;
        /** @type {EventEmitter} game.events compat */
        this.events = new EventEmitter();
        /** Phaser game.sound compat stub */
        this.sound = { play: () => { }, add: () => ({ play: () => { }, stop: () => { } }), volume: 1 };
    }

    /**
     * Phaser.Game 생성자 대체
     * @param {{ parent: HTMLElement, width: number, height: number, transparent?: boolean }} config
     */
    async init(config) {
        const app = new Application();
        await app.init({
            width: config.width,
            height: config.height,
            backgroundAlpha: config.transparent ? 0 : 1,
            backgroundColor: config.backgroundColor || 0x0a0a1a,
            resolution: config.resolution || 1,
            autoDensity: true,
            antialias: true,
            // Canvas is appended to parent
        });

        // Attach canvas to parent
        if (config.parent) {
            config.parent.appendChild(app.canvas);
        }

        this.app = app;
        this.canvas = app.canvas;
        this.isRunning = true;

        // Scale management (FIT mode like Phaser)
        this._setupScaling(config.parent, config.width, config.height);

        return this;
    }

    // ── Registry (game.registry compat) ──
    get registry() {
        const self = this;
        return {
            set(key, val) { self._registry.set(key, val); },
            get(key) { return self._registry.get(key); },
        };
    }

    // ── Scale ──
    get scale() {
        return {
            gameSize: {
                width: this.app?.screen?.width || 1280,
                height: this.app?.screen?.height || 720,
            },
        };
    }

    // ── Scene management (game.scene compat) ──
    get scene() {
        const self = this;
        return {
            getScene(key) { return self.scenes.get(key) || null; },
            getScenes(activeOnly = false) {
                const all = [...self.scenes.values()];
                return activeOnly ? all.filter(s => s._isActive) : all;
            },
            start(key) {
                const scene = self.scenes.get(key);
                if (scene) scene._start();
            },
            stop(key) {
                const scene = self.scenes.get(key);
                if (scene) scene._stop();
            },
        };
    }

    /**
     * Register a scene instance
     * @param {string} key
     * @param {import('./SceneCompat').SceneCompat} scene
     */
    addScene(key, scene) {
        scene._engine = this;
        scene._key = key;
        this.scenes.set(key, scene);
    }

    _setupScaling(parent, baseW, baseH) {
        if (!parent || !this.app) return;

        const resize = () => {
            const pw = parent.clientWidth || window.innerWidth;
            const ph = parent.clientHeight || window.innerHeight;
            const scale = Math.min(pw / baseW, ph / baseH);
            const canvas = this.app.canvas;
            canvas.style.width = `${baseW * scale}px`;
            canvas.style.height = `${baseH * scale}px`;
            canvas.style.position = 'absolute';
            canvas.style.left = `${(pw - baseW * scale) / 2}px`;
            canvas.style.top = `${(ph - baseH * scale) / 2}px`;
        };

        resize();
        window.addEventListener('resize', resize);
        this._resizeHandler = resize;
    }

    destroy() {
        // Stop all scenes
        for (const scene of this.scenes.values()) {
            scene._stop();
        }
        // Remove resize listener
        if (this._resizeHandler) {
            window.removeEventListener('resize', this._resizeHandler);
        }
        // Destroy PixiJS app
        if (this.app) {
            this.app.destroy(true, { children: true });
        }
        this.isRunning = false;
        this.canvas = null;
    }
}
